import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, pairwise, skip, tap } from 'rxjs/operators';
import { CriteriaService } from 'src/app/services/index.service';
import { ISingleMultiOptionQuestion } from 'src/app/shared/interfaces/index.interface';
import { hatchFade } from '../hatch-skin/hatch-animation';

interface IOption{
  id:number;
  type:string;
  additional_fields:{text:string};
}
interface IData{
  isUnknown:boolean;
  comment:string|null;
}
interface IAnswer{
  id:number;
  answer:number[];
  valid:boolean;
  isUnknown:boolean;
}

@Component({
  selector: 'app-multi-option',
  templateUrl: './multi-option.component.html',
  styleUrls: ['./multi-option.component.scss'],
  animations: [hatchFade],
})
export class MultiOptionComponent implements OnInit {
  @Input()data:ISingleMultiOptionQuestion = <ISingleMultiOptionQuestion>{};
  @Input()questionNumber:number = -1;
  @Input()config:any;
  @Input()nextPage$:Observable<boolean> = new Observable<boolean>();
  @Input()answers:IAnswer[] = []
  @Output()dataEmitter:EventEmitter<any> = new EventEmitter<any>();
  
  checkFormGroup:boolean = false;
  outputData:IAnswer = {
    id: 0,
    answer: [],
    valid: false,
    isUnknown: false,
  }
  formGroup:UntypedFormGroup = new UntypedFormGroup({})
  commentStatus:BehaviorSubject<boolean> = new BehaviorSubject(false)
  commentStatusObs$:Observable<boolean> = this.commentStatus.asObservable()

  extraData:IData = <IData>{};
  answer:IOption[] = [];
  question:IOption[] = [];

  activeOptionStyle:any = {};
  hoverOptionStyle:any = {};
  hoverNum:number = -1;

  isUnknownInital:boolean|null = null;

  constructor(
    private criteriaService:CriteriaService,
    private toastr:ToastrService,
  ) { }

  ngOnInit(): void {
    this.setStyles()
    this.configFormControls()
    this.check()
    this.nextPageAction()
  }
  setStyles(){
    this.activeOptionStyle = {
      'background-color': this.data.additional_fields.background_color,
      'color': this.data.additional_fields.text_color
    }
    this.hoverOptionStyle = {
      'background-color': this.data.additional_fields.background_color,
      'color': this.data.additional_fields.text_color,
      'opacity': '0.5',
    }
  }
  configFormControls(){
    this.data.options.forEach((element:any, i:number)=>{
      this.formGroup.addControl(i.toString(), new UntypedFormControl(false))
    })
    this.getInitialData()
  }
  changeCommentState(){
    this.commentStatus.next(!this.commentStatus.value)
  }
  updateExpandSecData(data:IData){
    this.extraData = data
  }
  sendComment(){
    let form:FormData = new FormData()
    form.append('comment', JSON.stringify(this.extraData.comment))
    this.criteriaService.sendQuestionComment(this.config.quizUserName, this.data.id, form).subscribe(
      ()=>{
        this.commentStatus.next(!this.commentStatus.value)
        this.toastr.success('نظر شما با موفقیت ارسال گردید')
      },
      ()=>{
        this.toastr.error('مشکلی در ارسال نظر شما پیش آمده')
      }
    )
  }
  check(){
    if(!this.data.additional_fields.is_several_option)
      this.checkSingleOptionForAnswer()
    else
      this.checkMaxNumberOfOptions()
  }
  checkSingleOptionForAnswer(){
    this.formGroup.valueChanges.pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      pairwise(),
      tap(i=>this.setPrevActiveOptionToFalse(i))
    ).subscribe()
  }
  setPrevActiveOptionToFalse(val:Object[]){
    let prev = Object.values(val[0])
    let curr = Object.values(val[1])
    if(curr.filter(i=>i==true).length>1)
      for(let i=0; i<prev.length; i++){
        if(prev[i] && curr[i])
          this.formGroup.controls[i.toString()].setValue(false)
      }
  }
  checkMaxNumberOfOptions(){
    this.formGroup.valueChanges.pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      tap((i)=>{
        if(Object.values(i).filter(val=>val===true).length>=this.data.additional_fields.maximum_answer)
          this.disableFalseOptions()
        else
          this.enableFalseOptions()
      })
    ).subscribe()
  }
  disableFalseOptions(){
    Object.entries(this.formGroup.value).forEach(element=>{
      if(!element[1])
        this.formGroup.controls[element[0]].disable()
    })
  }
  enableFalseOptions(){
    Object.entries(this.formGroup.getRawValue()).forEach(element=>{
      if(!element[1])
        this.formGroup.controls[element[0]].enable()
    })
  }
  getFormControlValue(id:number):boolean{
    return this.formGroup.controls[id.toString()].value
  }
  changeFormControlValue(id:number):void{
    this.formGroup.controls[id.toString()].setValue(!this.getFormControlValue(id))
  }
  sendOutputData(nextPage:boolean){
    let answer:number[] = this.convertFormGroupToAnswer()
    this.outputData.id = this.data.id;
    this.outputData.isUnknown = this.extraData.isUnknown?true:false;
    this.outputData.answer = answer
    this.outputData.valid = answer.length>=this.data.additional_fields.minimum_answer
    if(this.outputData.isUnknown)
      this.outputData.valid = true;
    this.checkFormGroup = nextPage;

    this.dataEmitter.emit(this.outputData)
  }
  nextPageAction(){
    this.nextPage$.pipe(
      map(i=>this.sendOutputData(i)),
    ).subscribe()
  }
  convertFormGroupToAnswer():number[]{
    let answer:number[] = []
    Object.values(this.formGroup.value).forEach((element, i) => {
      if(element)
        answer.push(this.data.options[i].id)
    });
    return answer;
  }
  getErrorStyle(){
    return {
      'box-shadow': this.checkFormGroup&&this.convertFormGroupToAnswer().length<this.data.additional_fields.minimum_answer&&this.data.is_answer_required?'0 0 2px 2px rgba(255, 0, 0, 0.507)':'',
      'background-color': this.checkFormGroup&&this.convertFormGroupToAnswer().length<this.data.additional_fields.minimum_answer&&this.data.is_answer_required?'rgb(255 1 1 / 10%)':'',
    }
  }
  getInitialData(){
    let answerIndex = this.answers.findIndex(element=>element.id == this.data.id)
    if(answerIndex>-1){
      this.isUnknownInital = this.answers[answerIndex].isUnknown
      let answersId:number[] = this.answers[answerIndex].answer
      answersId.forEach(id=>{
        let index:number = this.data.options.findIndex(option=>option.id===id)
        if(index>-1)
          this.formGroup.controls[index.toString()].setValue(true)
      })
    }
    this.formGroup.valueChanges.pipe(
      debounceTime(100),
      map(i=>{
        if(this.config.isRealTime)
          this.sendOutputData(false)
      })
    ).subscribe()
  }
}
